import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BaseButton from "../BaseButton";
import BaseIcon from "../BaseIcon";
import { MODAL_TYPES } from "../../utils/emitter";
import useModalActions from "../../hooks/useModalActions";
import Pricing from "../../store/Pricing";
import Points from "../../store/Points";
import PersonalAccount from "../../store/PersonalAccount";
import ROUTES from "../../utils/routes";

// Пара кнопок «деньги / баллы». Раньше жила только внутри BuyProductModal, но
// 21.09 такие же кнопки понадобились ПРЯМО на странице описания анкеты — чтобы
// логика оплаты не разъехалась между попапом и страницей, вынесена сюда.
// onBefore — то, что нужно сделать перед уходом на оплату (в попапе это закрытие
// модалки; на странице не нужно ничего).
export default observer(function BuyMoneyPointsRow({
  productType,
  gameId,
  from,
  onPurchased,
  onBefore,
  className = "buy-product-modal__actions",
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModalActions();

  useEffect(() => {
    Pricing.fetch();
    if (Points.balance === null) Points.fetchBalance();
  }, []);

  const price = productType ? Pricing.get(productType) : null;
  const canPayWithPoints = Boolean(price && price.points !== null);
  // отличаем «баланс ещё не загружен» от «баллов реально мало»
  const balanceLoaded = Points.balance !== null;
  const enoughPoints =
    canPayWithPoints && balanceLoaded && Points.balance >= price.points;

  const buyWithMoney = () => {
    onBefore && onBefore();
    navigate(ROUTES.PAYMENT_PAGE, {
      state: { productType, gameId, from },
    });
  };

  const buyWithPoints = () => {
    // баланс ещё грузится — не уводим в «не хватает баллов», просто ждём
    if (!balanceLoaded) return;
    if (!enoughPoints) {
      onBefore && onBefore();
      openModal(MODAL_TYPES.NOT_ENOUGH_POINTS, { productType });
      return;
    }
    // «покупка игры» = апгрейд демо: шлём DEMO_GAME, зеркально денежному пути
    Points.spend(
      productType === "GAME" ? "DEMO_GAME" : productType,
      { gameId },
      async () => {
        onBefore && onBefore();
        await PersonalAccount.getPersonalAccountData();
        onPurchased && onPurchased();
      },
      (error) => {
        onBefore && onBefore();
        const code = error?.response?.data?.message?.code;
        if (code === "INSUFFICIENT_POINTS") {
          openModal(MODAL_TYPES.NOT_ENOUGH_POINTS, { productType });
        } else if (code === "CONNECT_REQUIRED") {
          openModal(MODAL_TYPES.CREATE_TEST_INFO);
        } else if (code === "FULFILLMENT_FAILED") {
          openModal(MODAL_TYPES.PURCHASE_FAILED);
        }
      }
    );
  };

  if (!productType || !price) return null;

  return (
    <div className={className}>
      <BaseButton onClick={buyWithMoney}>
        {t("loyalty.buy_for", { price: Pricing.format(productType) })}
      </BaseButton>
      {canPayWithPoints ? (
        <BaseButton
          onClick={buyWithPoints}
          unfilledGradient={enoughPoints}
          unfilledGray={!enoughPoints}
          disabled={!enoughPoints || Points.isSpending || !balanceLoaded}
        >
          <span className="buy-product-modal__points">
            <BaseIcon
              name="star"
              className={`buy-product-modal__points-star${
                enoughPoints ? "" : " buy-product-modal__points-star--muted"
              }`}
            />
            {price.points}
          </span>
        </BaseButton>
      ) : null}
    </div>
  );
});
